import { ArrowBack, Logout } from "@mui/icons-material";
import { Box, Button, styled, Typography } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { ReactComponent as Astronaut } from "assets/icons/astronaut.svg";
import { signOutEverywhere } from "helpers/signOutEverywhere";
import { useAuthStatus } from "helpers/useAuthStatus";
import { useHistory, useLocation } from "react-router-dom";

export type ForbiddenReason = "student";

interface ForbiddenProps {
	reason?: ForbiddenReason;
}

// Kept separate so the Keycloak hook only mounts when a student is actually signed in.
const SwitchAccountButton = (): JSX.Element => {
	const { keycloak } = useKeycloak();
	const { setRole } = useAuthStatus();

	return (
		<Button
			onClick={() => signOutEverywhere(keycloak, setRole)}
			color="primary"
			variant="contained"
			startIcon={<Logout />}
		>
			Log out and switch account
		</Button>
	);
};

const PREFIX = "Forbidden";

const classes = {
	astronaut: `${PREFIX}-astronaut`,
};

const StyledBox = styled(Box)(({ theme }) => ({
	[`& .${classes.astronaut}`]: {
		animation: `float 3000ms ${theme.transitions.easing.easeInOut} infinite`,
		"@keyframes float": {
			"0%": {
				transform: "translateY(0px)",
			},
			"50%": {
				transform: "translateY(-20px)",
			},
			"100%": {
				transform: "translateY(0px)",
			},
		},
	},
}));

export const Forbidden = ({ reason }: ForbiddenProps): JSX.Element => {
	const history = useHistory();
	const location = useLocation<{ reason?: ForbiddenReason } | undefined>();

	const status = location.pathname.includes("/404") ? 404 : 403;
	const isStudent = (reason ?? location.state?.reason) === "student";

	return (
		<StyledBox
			display="flex"
			flexDirection="row"
			justifyContent="center"
			p={1}
			m={1}
			flexWrap="wrap"
		>
			<Box p={1} width={1 / 2 || 300}>
				<Astronaut className={classes.astronaut} />
			</Box>
			<Box p={1} width={1 / 2} minWidth={800} alignSelf="center">
				<Box display="flex" flexDirection="column" justifyContent="left">
					<Typography variant="h1" align="left" style={{ fontWeight: 900 }}>
						{status}
					</Typography>
					<br />
					<Typography variant="h2" align="left">
						{isStudent
							? "This portal is for teachers"
							: "Oops! Looks like you’re lost!"}
					</Typography>
					<br />
					<Typography variant="subtitle1" align="left">
						{isStudent
							? "You’re signed in with a student account. Log out, then sign in with a teacher, parent or administrator account to use the Teacher Portal."
							: "The location you requested could not be found."}
					</Typography>
					<br />
					<Box display="flex" width="min-width" alignItems="left" gap={2}>
						{isStudent && <SwitchAccountButton />}
						<Button
							onClick={() => history.go(-1)}
							color="primary"
							variant={isStudent ? "outlined" : "contained"}
							startIcon={<ArrowBack />}
						>
							Back
						</Button>
					</Box>
				</Box>
			</Box>
		</StyledBox>
	);
};
